import { useCallback, useMemo } from 'react';
import { type SelectionMap } from '@/core/components/FilterSelector/types';

interface UseDefaultAllSelectionParams<T> {
  items: T[];
  selection: SelectionMap;
  touched: boolean;
  onCommit: (nextSelection: SelectionMap) => void;
  getKey?: (item: T) => string;
}

interface UseDefaultAllSelectionReturn {
  effectiveSelection: SelectionMap;
  handleChange: (updater: React.SetStateAction<SelectionMap>) => void;
}

/**
 * Hook that implements the "default all selected" pattern for filter selections.
 * When a selection is untouched and empty, it defaults to all items selected.
 * Once the user interacts with it, the selection becomes explicit.
 * 
 * @param items - Available items to select from
 * @param selection - Current selection state
 * @param touched - Whether the user has interacted with this selection
 * @param onCommit - Callback to persist the new selection and mark as touched
 * @param getKey - Optional function to extract key from item (defaults to String)
 */
export function useDefaultAllSelection<T = string>({
  items,
  selection,
  touched,
  onCommit,
  getKey = String,
}: UseDefaultAllSelectionParams<T>): UseDefaultAllSelectionReturn {
  const allSelectedMap = useMemo<SelectionMap>(
    () => Object.fromEntries(items.map((item) => [getKey(item), 'add' as const])),
    [items, getKey]
  );

  const shouldUseDefaultAll = !touched && Object.keys(selection).length === 0 && items.length > 0;

  const effectiveSelection = useMemo<SelectionMap>(
    () => (shouldUseDefaultAll ? allSelectedMap : selection),
    [shouldUseDefaultAll, allSelectedMap, selection]
  );

  const handleChange = useCallback(
    (updater: React.SetStateAction<SelectionMap>) => {
      const base = shouldUseDefaultAll ? allSelectedMap : selection;
      const nextSelection = typeof updater === 'function' ? updater(base) : updater;
      onCommit(nextSelection);
    },
    [shouldUseDefaultAll, allSelectedMap, selection, onCommit]
  );

  return { effectiveSelection, handleChange };
}
